import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@totalsoft/rocket-webapp-components-deprecated";
import { Button, Grid, makeStyles, Paper, Typography } from "@material-ui/core";
import PropTypes from "prop-types";
import { GetApp, MailOutline } from "@material-ui/icons";
import {
  useClientQueryWithErrorHandling,
  useMutationWithErrorHandling,
  useQueryWithErrorHandling,
} from "hooks/errorHandling";
import { PRODUCT_DOCUMENT } from "../queries";
import { DOWNLOAD_PRODUCTS_QUERY } from "features/products/queries";
import { convertBase64ToFile } from "utils/fileConverter";
import fileSaver from "file-saver";
import ActionsDialog from "features/common/components/ActionsDialog";
import * as V from "@totalsoft/pure-validations";
import EmailAddressContent from "features/common/components/EmailAddressContent";
import { SEND_PRODUCTS_ON_EMAIL } from "features/products/mutations";
import { addToast } from "components/notifications";
import styles from "../styles";

const useStyles = makeStyles(styles);

function ProductDocument({ quoteId, disabledByTeg }) {
  const { t } = useTranslation();
  const classes = useStyles();
  const clientQuery = useClientQueryWithErrorHandling();
  const [openDialog, setOpenDialog] = useState(false);

  const { data } = useQueryWithErrorHandling(PRODUCT_DOCUMENT, { variables: { quoteId } });
  const productDocument = data?.productDocument;

  const handleOpenDialog = useCallback(() => {
    setOpenDialog(true);
  }, []);

  const [sendProductsOnEmail] = useMutationWithErrorHandling(SEND_PRODUCTS_ON_EMAIL, {
    onCompleted: () => {
      addToast(t("ToastNotifications.EmailMessageWasSent"), "success");
    },
  });

  const handleSendViaEmail = useCallback(
    (email, products) => {
      sendProductsOnEmail({ variables: { quoteId: quoteId, email, products } });
    },
    [sendProductsOnEmail, quoteId]
  );

  const handleDownload = useCallback(async () => {
    const { data } = await clientQuery(DOWNLOAD_PRODUCTS_QUERY, {
      variables: { quoteId: quoteId, products: [productDocument?.quoteProductId] },
      fetchPolicy: "no-cache",
    });

    if (!data) return;

    const fileURL = convertBase64ToFile(data.downloadProducts.content);
    fileSaver.saveAs(fileURL, data.downloadProducts.name);
  }, [clientQuery, quoteId, productDocument]);

  return (
    <Card>
      <Paper className={classes.paper}>
        <Grid container spacing={1} alignItems="center" justifyContent="center">
          <Grid item xs={12}>
            <Typography variant="h4">{t("DataCompletion.SelectedProductDocument")}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h5">{t("DataCompletion.SelectedProductDocumentSubtitle")}</Typography>
          </Grid>
          {productDocument && (
            <Grid
              item
              container
              xs={12}
              justifyContent="space-between"
              alignItems="center"
              className={classes.firstReqDocBorder}
            >
              <Grid item>
                <Typography variant="h6" className={classes.reqText}>
                  {productDocument?.caption}
                </Typography>
              </Grid>
              <Grid item>
                <Button
                  id="productDocument_email"
                  startIcon={<MailOutline />}
                  disabled={disabledByTeg}
                  onClick={handleOpenDialog}
                >
                  {t("Product.SendViaEMAIL")}
                </Button>
                <Button
                  id="productDocument_download"
                  startIcon={<GetApp />}
                  disabled={disabledByTeg}
                  onClick={handleDownload}
                >
                  {t("Product.Download")}
                </Button>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Paper>
      <ActionsDialog
        open={openDialog}
        handleClose={useCallback(() => setOpenDialog(false), [])}
        title={t("Product.ProvideUsEmailAddress")}
        validator={V.shape({ email: [V.required, V.email] |> V.stopOnFirstFailure })}
        actions={[
          {
            caption: t("General.Confirm"),
            handle: (state, validate) => {
              if (!validate(state)) return;
              handleSendViaEmail(state.email, [productDocument.quoteProductId]);
              return true;
            },
          },
        ]}
        Content={EmailAddressContent}
      />
    </Card>
  );
}

ProductDocument.propTypes = {
  quoteId: PropTypes.number.isRequired,
  disabledByTeg: PropTypes.bool.isRequired,
};

export default ProductDocument;
